import { z } from 'zod';
import { timestamp, uuid } from './common.js';
import { policyCategory } from './errors.js';
import { listQueryInput, paginated } from './list.js';
/**
 * Moderation (§6.8, F74). ONE runtime contract + ONE persisted projection.
 */
/**
 * `moderationResult` — the runtime/wire/worker-return contract. `severity` and
 * `raw` are runtime-only: `raw` is NEVER persisted or shown; `severity` never
 * becomes a `moderation_check` column.
 */
export const moderationResult = z.object({
    allowed: z.boolean(),
    violatedTerms: z.array(policyCategory), // matched categories; [] when allowed
    severity: z.enum(['low', 'medium', 'high']).optional(),
    raw: z.unknown().optional(),
});
/** Persisted verdict (`moderation_check.verdict`). */
export const moderationVerdict = z.enum(['pass', 'flag', 'block']);
/**
 * `moderation_check` — persisted projection of a classification (book=document).
 * `flag` routes to the F74 human-review queue (`human_reviewed=false` until
 * closed); `block` emits `CONTENT_POLICY_VIOLATION` with NO credit debit.
 */
export const moderationCheckSchema = z.object({
    id: uuid,
    documentId: uuid, // book_id FK documents.id
    chapterId: z.string().nullable(), // outline/chapter node; null for whole-doc/share checks
    model: z.string().min(1),
    verdict: moderationVerdict,
    categories: z.array(policyCategory), // = violatedTerms
    humanReviewed: z.boolean(),
    checkedAt: timestamp,
});
// --- F74 moderator human-review queue -----------------------------------------
export const moderationQueueSortColumns = ['checkedAt'];
/** `queue.list` — moderator/admin only, server-gated (hidden entirely from non-moderators, F74 AC). Fixed to the flagged/open slice; oldest-first SLA ordering is the default sort direction. */
export const moderationQueueListQuery = listQueryInput(moderationQueueSortColumns, {
    defaultSort: 'checkedAt',
    defaultDir: 'asc',
});
/** One `queue.list` row — the flagged `moderation_check` plus enough asset context to review without a second round trip. `shareId` is null for a `gate='generate'` check (whole-doc/chapter pre-draft gate has no share yet). */
export const moderationQueueRow = moderationCheckSchema.extend({
    documentTitle: z.string(),
    ownerId: uuid,
    ownerDisplayName: z.string().nullable(),
    shareId: uuid.nullable(),
});
export const moderationQueueListResult = paginated(moderationQueueRow);
/** `pass` auto-allows and `block` auto-refuses BEFORE reaching this queue (§6.8) — a human only ever sees a `flag` row, so the decision is binary. */
export const moderationDecision = z.enum(['approve', 'block']);
/**
 * `queue.resolve` — closes ONE `moderation_check` row (`humanReviewed` flips
 * to `true`; re-flagging after an edit creates a fresh row per F74 AC,
 * never reopens this one). `approve` = false positive → publish/allow (the
 * `pass` path). `block` REQUIRES a `reasonCategory` and routes through the
 * same F69 takedown mechanics (owner-notified, audited) — never a silent
 * removal.
 */
export const moderationQueueResolveInput = z
    .object({
    moderationCheckId: uuid,
    decision: moderationDecision,
    reasonCategory: policyCategory.optional(),
    reasonNote: z.string().trim().max(1000).optional(),
})
    .refine((v) => v.decision !== 'block' || v.reasonCategory !== undefined, {
    message: 'errors:moderation.queue.reason.required',
    path: ['reasonCategory'],
});
export const moderationQueueResolveResult = moderationCheckSchema;
// --- F69 moderator takedown ----------------------------------------------------
export const takedownTargetType = z.enum(['share', 'comment']);
/**
 * `moderation.takedown` — moderator/admin only, PUBLIC content ONLY
 * (server-enforced fail-closed, never a UI-only gate — §15: "moderator
 * scope = PUBLIC content only"). `reasonCategory` is REQUIRED and drawn from
 * the same closed `policyCategory` vocabulary as every other refusal —
 * never an invented ad hoc string (F69 AC: "policy vocabulary from
 * 0.8/F60, never invented"). Reversible by an admin (audited re-publish is a
 * separate, out-of-scope-for-this-op admin action, F69 AC).
 */
export const takedownInput = z.object({
    targetType: takedownTargetType,
    targetId: uuid,
    reasonCategory: policyCategory,
    reasonNote: z.string().trim().min(1, 'errors:takedown.reason.required').max(1000).optional(),
});
export const takedownResult = z.object({
    targetType: takedownTargetType,
    targetId: uuid,
    removedAt: timestamp,
});
